import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PenTool, Upload, Check, X, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, NativeSelect, Empty } from "@/components/common";
import { MapCanvas, MapLegend } from "@/components/map/MapCanvas";
import { AreaPanel } from "@/components/map/AreaPanel";
import { CriticitaFormDialog, useMapMeta } from "@/components/CriticitaFormDialog";
import { OrderFormDialog } from "@/components/OrderFormDialog";

const NewAreaDialog = ({ open, onOpenChange, types, onSave }) => {
  const [label, setLabel] = useState("");
  const [type, setType] = useState("campo");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="new-area-dialog">
        <DialogHeader><DialogTitle>Nuova area</DialogTitle></DialogHeader>
        <div className="space-y-3"><div className="space-y-1"><Label>Nome *</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="new-area-label" /></div>
          <div className="space-y-1"><Label>Tipo</Label><NativeSelect className="w-full" testid="new-area-type" value={type} onChange={setType}>{Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></div></div>
        <DialogFooter><Button disabled={!label} onClick={() => { onSave(label, type); setLabel(""); }} className="rounded-xl font-bold" data-testid="new-area-save">Salva area</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Mappe() {
  const { cimiteroId } = useParams();
  const nav = useNavigate();
  const meta = useMapMeta();
  const [cims, setCims] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [m, setM] = useState(null);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState("view");
  const [draft, setDraft] = useState([]);
  const [editPts, setEditPts] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [ver, setVer] = useState(0);
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;

  useEffect(() => {
    api.get("/commesse").then((r) => { setCommesse(r.data); setCims(r.data.flatMap((c) => c.cimiteri)); });
  }, []);
  useEffect(() => {
    if (!cimiteroId && cims.length) nav(`/mappe/${(cims.find((c) => c.has_plan) || cims[0]).id}`, { replace: true });
  }, [cimiteroId, cims, nav]);
  const load = useCallback(() => {
    if (cimiteroId) api.get(`/cimiteri/${cimiteroId}/map`).then((r) => setM(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cimiteroId]);
  useEffect(() => {
    setM(null); setSel(null); setMode("view");
    load();
  }, [load]);

  const refresh = () => { load(); setVer((v) => v + 1); };
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/cimiteri/${cimiteroId}/planimetria`, fd);
      toast.success("Planimetria caricata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveNew = async (label, type) => {
    try {
      await api.post("/aree", { cimitero_id: cimiteroId, label, type, points: draft });
      toast.success("Area creata");
      setDraft([]); setMode("view"); setDlg(null); load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/aree/${sel.id}`, { cimitero_id: cimiteroId, label: sel.label, type: sel.type, points: editPts, notes: sel.notes || "" });
      toast.success("Geometria salvata");
      setMode("view"); setEditPts(null); refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const startEdit = () => { setEditPts(sel.points); setMode("edit"); };
  const cimitero = cims.find((c) => c.id === cimiteroId);

  const panel = sel && mode === "view" && (
    <AreaPanel areaId={sel.id} version={ver} onReport={() => setDlg("cr")} onCreateOrder={() => setDlg("order")} onEdit={startEdit}
      onArchived={() => { setSel(null); load(); }} />
  );

  return (
    <div data-testid="mappe-page">
      <PageHeader title="Mappe dei cimiteri" subtitle={m ? `${m.areas.length} aree · ${m.areas.filter((a) => a.state === "red").length} con criticità aperte` : ""}
        actions={<>
          <NativeSelect testid="map-cimitero-select" value={cimiteroId} onChange={(v) => nav(`/mappe/${v}`)}>{cims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect>
          {m?.can_edit && <label className="inline-flex h-10 cursor-pointer items-center gap-1 rounded-xl border bg-white px-3 text-sm font-semibold" data-testid="map-upload-plan">
            <Upload className="h-4 w-4" />{m.planimetria ? "Sostituisci planimetria" : "Carica planimetria"}
            <input type="file" accept="image/*,application/pdf,.svg" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} /></label>}
          {m?.can_edit && m.planimetria && mode === "view" && <Button onClick={() => { setSel(null); setDraft([]); setMode("draw"); }} className="h-10 rounded-xl font-bold" data-testid="map-draw-button"><PenTool className="mr-1 h-4 w-4" />Nuova area</Button>}
        </>} />
      <div className="mb-3"><MapLegend /></div>
      {mode === "draw" && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl bg-blue-50 p-3 text-sm" data-testid="map-draw-toolbar">
          <b>Disegno area:</b> tocca la planimetria per aggiungere i vertici ({draft.length}).
          <Button size="sm" variant="outline" onClick={() => setDraft(draft.slice(0, -1))} className="rounded-xl"><Undo2 className="mr-1 h-3 w-3" />Annulla vertice</Button>
          <Button size="sm" disabled={draft.length < 3} onClick={() => setDlg("newarea")} className="rounded-xl" data-testid="map-draw-complete"><Check className="mr-1 h-3 w-3" />Completa</Button>
          <Button size="sm" variant="ghost" onClick={() => { setMode("view"); setDraft([]); }} className="rounded-xl"><X className="mr-1 h-3 w-3" />Esci</Button>
        </div>
      )}
      {mode === "edit" && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl bg-blue-50 p-3 text-sm" data-testid="map-edit-toolbar">
          <b>Modifica «{sel.label}»:</b> trascina i vertici blu.
          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => { const a = editPts[editPts.length - 1], b = editPts[0]; setEditPts([...editPts, [Math.round((a[0] + b[0]) / 2), Math.round((a[1] + b[1]) / 2)]]); }} data-testid="map-add-vertex">Aggiungi vertice</Button>
          <Button size="sm" variant="outline" className="rounded-xl" disabled={editPts.length <= 3} onClick={() => setEditPts(editPts.slice(0, -1))}>Rimuovi ultimo</Button>
          <Button size="sm" onClick={saveEdit} className="rounded-xl" data-testid="map-edit-save"><Check className="mr-1 h-3 w-3" />Salva</Button>
          <Button size="sm" variant="ghost" onClick={() => { setMode("view"); setEditPts(null); }} className="rounded-xl">Annulla</Button>
        </div>
      )}
      {m && !m.planimetria && <Empty text={m.can_edit ? "Nessuna planimetria: caricala in PDF, immagine o SVG." : "Planimetria non ancora disponibile."} testid="map-no-plan" />}
      {m?.planimetria && (
        <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
          <MapCanvas plan={m.planimetria} areas={m.areas} selectedId={sel?.id} onSelect={setSel} mode={mode} draft={draft}
            onCanvasPoint={(p) => setDraft([...draft, p])} editPoints={editPts} onEditPoints={setEditPts} />
          {isDesktop && <aside className="card-soft max-h-[78vh] overflow-y-auto p-5">{panel || <p className="text-sm text-muted-foreground">Seleziona un'area sulla mappa per vederne la scheda.</p>}</aside>}
        </div>
      )}
      {!isDesktop && (
        <Sheet open={!!panel} onOpenChange={(o) => !o && setSel(null)}>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl"><SheetHeader><SheetTitle className="sr-only">Scheda area</SheetTitle></SheetHeader>{panel}</SheetContent>
        </Sheet>
      )}
      {meta && <NewAreaDialog open={dlg === "newarea"} onOpenChange={(o) => setDlg(o ? "newarea" : null)} types={meta.area_types} onSave={saveNew} />}
      <CriticitaFormDialog open={dlg === "cr"} onOpenChange={(o) => setDlg(o ? "cr" : null)} preset={{ cimitero_id: cimiteroId, area_id: sel?.id }} onCreated={() => { setDlg(null); refresh(); }} />
      {cimitero && <OrderFormDialog open={dlg === "order"} onOpenChange={(o) => setDlg(o ? "order" : null)} commesse={commesse}
        preset={{ commessa_id: cimitero.commessa_id, cimitero_id: cimiteroId, area_id: sel?.id, area: sel?.label }} />}
    </div>
  );
}
